import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { usePageTitle } from "hooks/usePageTitle";
import {
  Box,
  IconButton,
  useTheme,
  ListItemIcon,
  Typography,
  Divider,
  Button,
  Avatar,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import ColumnFilter from "components/ColumnFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import { DataGrid, GridToolbarContainer, gridClasses, useGridApiRef } from "@mui/x-data-grid";
import CEQuickFilter from "components/CEQuickFilter";
import { useStateContext } from "contexts/ContextProvider";
import { useWebSocket } from "contexts/WebSocketContext";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import Grid from "@mui/material/Grid2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { EditRounded } from "@mui/icons-material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import { getAvatarColor } from "helpers/avatarHelpers";
import HourlyRateHoverCell from "components/HourlyRateHoverCell";

const Members = () => {
  usePageTitle("Members");
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);

  const [listofMembers, setListOfMembers] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const apiRef = useGridApiRef();
  // Preserve the scroll position across refetches (saves bounce to the top)
  const { capture: captureGridScroll, restore: restoreGridScroll } =
    useDataGridScrollRestore(apiRef);

  const { setOverlayData, setOverlayName } = useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();
  const { socket } = useWebSocket();

  const [contextMenu, setContextMenu] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    const upperFieldName = fieldName.toUpperCase();
    return `MEMBERS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback((fieldName, values) => {
    const key = getFilterStorageKey(fieldName);
    if (values && values.length > 0) {
      localStorage.setItem(key, JSON.stringify(values));
    } else {
      localStorage.removeItem(key);
    }
  }, [getFilterStorageKey]);

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback((fieldName) => {
    const key = getFilterStorageKey(fieldName);
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : [];
  }, [getFilterStorageKey]);

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    const fields = ["Name", "Email", "Role", "HourlyRate"];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = ["Name", "Email", "Role", "HourlyRate"];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const MEMBERS_FILTER_KEY = "MEMBERS_FILTER_MODEL";

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(MEMBERS_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
  };

  const handleContextMenuEdit = () => {
    const memberData = listofMembers.find(
      (member) => member.id === selectedRow,
    );

    const secondData = {
      MemberID: selectedRow,
      Member: memberData,
      Action: OVERLAY_NAMES.MEMBER_ACTION_EDIT,
    };

    setOverlayData(secondData);
    setOverlayName(OVERLAY_NAMES.MEMBER_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuSendEmail = () => {
    const selectedMember = listofMembers.find(
      (member) => member.id === selectedRow,
    );
    if (selectedMember && selectedMember.Email) {
      window.location.href = `mailto:${selectedMember.Email}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuCopyEmail = () => {
    const selectedMember = listofMembers.find(
      (member) => member.id === selectedRow,
    );
    if (selectedMember && selectedMember.Email) {
      navigator.clipboard
        .writeText(selectedMember.Email)
        .then(() => {
          showSnackbar("Email copied to clipboard", theme, "success");
        })
        .catch((err) => {
          console.error("Failed to copy email: ", err);
          showSnackbar("Failed to copy email", theme, "error");
        });
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const hasEmail = () => {
    const selectedMember = listofMembers.find(
      (member) => member.id === selectedRow,
    );
    return (
      selectedMember && selectedMember.Email && selectedMember.Email !== "N/A"
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      captureGridScroll();
      try {
        const response = await api.get("/members?includeInactive=true");
        setListOfMembers(response.data);
        restoreGridScroll(response.data);
      } catch (error) {
        showSnackbar("Error fetching members", theme, "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    refreshPage,
    refreshTrigger,
    showSnackbar,
    theme,
    captureGridScroll,
    restoreGridScroll,
  ]);

  // Listen for member updates
  useEffect(() => {
    if (!socket) return;

    const handleMemberUpdate = (data) => {
      setRefreshTrigger((prev) => prev + 1);
      const userName = data.updatedBy?.userName || "Unknown User";
      const message = `Member ${data.changeAction} by ${userName}`;
      showSnackbar(message, theme, "info");
    };

    socket.on("member:updated", handleMemberUpdate);
    return () => socket.off("member:updated", handleMemberUpdate);
  }, [socket, theme, showSnackbar]);

  // Listen for timesheet clock-in/out updates to refresh avatar glow in real time
  useEffect(() => {
    if (!socket) return;

    const handleTimesheetUpdated = ({ userId, action }) => {
      if (!userId || (action !== "clock-in" && action !== "clock-out")) return;

      setListOfMembers((prev) =>
        prev.map((member) =>
          Number(member.id) === Number(userId)
            ? { ...member, isClockedIn: action === "clock-in" }
            : member,
        ),
      );
    };

    socket.on("timesheet:updated", handleTimesheetUpdated);
    return () => socket.off("timesheet:updated", handleTimesheetUpdated);
  }, [socket]);

  const hasAppliedSavedFiltersRef = useRef(false);

  const columns = [
    {
      field: "ImageThumbnail",
      headerName: "Avatar",
      minWidth: 70,
      maxWidth: 90,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      cellClassName: "member-avatar-cell",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const imageData = params.row.ImageThumbnail;
        const isClockedIn = params.row.isClockedIn;
        const isTimesheetsHidden = Number(params.row.id) === 1;
        const hasClockStatus = !isTimesheetsHidden && isClockedIn !== undefined && isClockedIn !== null;
        const glowColor = isClockedIn
          ? theme.palette.primary.main
          : theme.palette.error.main;

        return (
          <Tooltip title={params.row.Name}>
            <Box
              sx={{
                position: "relative",
                display: "inline-flex",
                width: 40,
                height: 40,
              }}
            >
              <Avatar
                variant="rounded"
                src={imageData}
                alt={params.row.Name}
                sx={{
                  width: 40,
                  height: 40,
                  cursor: "pointer",
                  bgcolor: getAvatarColor(params.row.Name),
                  fontSize: "14px",
                  borderRadius: "12px",
                  boxShadow: hasClockStatus
                    ? `0 0 0 3px ${glowColor}, 0 0 12px ${glowColor}`
                    : theme.shadows[3],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  animation: hasClockStatus && !isClockedIn
                    ? "ceClockOutPulseScale 1.8s ease-in-out infinite"
                    : "none",
                  transformOrigin: "center center",
                  "@keyframes ceClockOutPulseScale": {
                    "0%": { transform: "scale(1)", opacity: 1 },
                    "50%": { transform: "scale(1.03)", opacity: 0.9 },
                    "100%": { transform: "scale(1)", opacity: 1 },
                  },
                }}
              >
                {params.row.Name?.charAt(0) || "U"}
              </Avatar>
              {hasClockStatus && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -1,
                    right: -1,
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    border: `2px solid ${glowColor}`,
                    backgroundColor: glowColor,
                    boxShadow: `0 0 10px ${glowColor}`,
                    animation: isClockedIn
                      ? "none"
                      : "ceClockOutPulseDotScale 1.8s ease-in-out infinite",
                    transformOrigin: "center center",
                    "@keyframes ceClockOutPulseDotScale": {
                      "0%": { transform: "scale(1)", opacity: 1 },
                      "50%": { transform: "scale(1.15)", opacity: 0.85 },
                      "100%": { transform: "scale(1)", opacity: 1 },
                    },
                  }}
                />
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "Name",
      headerName: "Name",
      minWidth: 200,
      flex: 2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData?.[0] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Email",
      headerName: "Email",
      minWidth: 250,
      flex: 2.5,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData?.[1] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Role",
      headerName: "Role",
      minWidth: 150,
      flex: 1.5,
      sortable: true,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData?.[2] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "HourlyRate",
      headerName: "Hourly Rate",
      minWidth: 160,
      flex: 1.5,
      sortable: true,
      valueGetter: (value) => Number.parseFloat(value || 0),
      renderCell: (params) => {
        const amount = Number.parseFloat(params.row?.HourlyRate || 0);
        return <HourlyRateHoverCell amount={amount} />;
      },
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData?.[3] || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState(getStoredFilterModel);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listofMembers || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Load saved column filters when data first becomes available
  useEffect(() => {
    if (!listofMembers || listofMembers.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });
    hasAppliedSavedFiltersRef.current = true;
  }, [listofMembers, loadAllColumnFilters]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback((field, values) => {
    handleColumnFilterChange(field, values);
    saveColumnFilter(field, values);
  }, [handleColumnFilterChange, saveColumnFilter]);

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(MEMBERS_FILTER_KEY, JSON.stringify({ items: [] }));
  }, [clearAllColumnFilters, clearAllFilters]);

  // Quick filter handling
  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(MEMBERS_FILTER_KEY, JSON.stringify(newFilterModel));
  };

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      { field: "Name", valueGetter: (row) => row.Name || "" },
      { field: "Email", valueGetter: (row) => row.Email || "" },
      { field: "Role", valueGetter: (row) => row.Role || "" },
      {
        field: "HourlyRate",
        valueGetter: (row) => {
          const amount = Number.parseFloat(row?.HourlyRate || 0);
          return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "$0.00";
        },
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listofMembers || [];
      }

      return (listofMembers || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listofMembers, filterColumns]);

  const handleSortModelChange = (newSortModel) => {
    //setSortModel(newSortModel);
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
    }) => {
      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {toolbarHasFilters && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ClearIcon />}
                onClick={onClear}
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <CEQuickFilter />
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme]);

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="flex-end"
            sx={{ pr: 1 }}
          >
            {isMobile ? (
              <Tooltip title="Add Member" placement="top">
                <IconButton
                  onClick={() => {
                    const data = {
                      Action: OVERLAY_NAMES.MEMBER_ACTION_NEW,
                    };

                    setOverlayData(data);
                    setOverlayName(OVERLAY_NAMES.MEMBER_NEW_EDIT);
                  }}
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  <AddRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                onClick={() => {
                  const data = {
                    Action: OVERLAY_NAMES.MEMBER_ACTION_NEW,
                  };

                  setOverlayData(data);
                  setOverlayName(OVERLAY_NAMES.MEMBER_NEW_EDIT);
                }}
                startIcon={<AddRoundedIcon />}
                sx={{
                  width: "200px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "12px",
                  boxShadow:
                    "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                  color: theme.palette.background.default,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  "& .MuiButton-startIcon": {
                    marginRight: 0,
                  },
                  "& .MuiButton-endIcon": {
                    display: "none",
                  },
                  "&:hover": {
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                    boxShadow:
                      "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                  },
                }}
              >
                <Box sx={{ flex: 1, textAlign: "center" }}>Add Member</Box>
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          boxShadow: theme.shadows[3],
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.member-avatar-cell`]: {
            display: "flex",
            alignItems: "center",
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          loading={isLoading}
          rowHeight={60}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          apiRef={apiRef}
          columns={columns}
          sortModel={sortModel}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={handleSortModelChange}
          disableColumnSorting
          disableColumnMenu
          rowSelectionModel={openFilter ? [] : undefined}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            const data = {
              Member: params.row,
              MemberID: params.row.id,
            };

            setOverlayData(data);
            navigate(`/members/${params.row.id}`, { state: data });
          }}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },

            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          slotProps={{
            paper: {
              sx: {
                bgcolor: theme.palette.background.alt,
                border: `1px solid ${theme.palette.divider}`,
                "& .MuiListItemIcon-root": {
                  color: theme.palette.primary.main,
                },
              },
            },
          }}
        >
          <MenuItem onClick={handleContextMenuEdit}>
            <ListItemIcon>
              <EditRounded fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuSendEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <EmailRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Send Email</Typography>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem
            onClick={handleContextMenuCopyEmail}
            disabled={!hasEmail()}
            sx={{
              opacity: hasEmail() ? 1 : 0.5,
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            <ListItemIcon>
              <ContentCopyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Copy Email</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default Members;
